import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Tooltip } from 'antd';
import { FileDoneOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { useNavigate } from 'react-router-dom';

const PendingReviewsBadge: React.FC = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [globalAwardsActive, setGlobalAwardsActive] = useState<boolean | null>(null);

  const fetchToken = useCallback(async (): Promise<string> => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return res.idToken;
  }, [instance, accounts]);

  useEffect(() => {
    const checkGlobalAwards = async () => {
      try {
        const token = await fetchToken();
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Rewards/isGlobalAwardsActive`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setGlobalAwardsActive(await res.json());
        else setGlobalAwardsActive(false);
      } catch {
        setGlobalAwardsActive(false);
      }
    };
    checkGlobalAwards();
  }, [fetchToken]);

  const fetchPendingCount = useCallback(async () => {
    const userEmail = localStorage.getItem('UserMail');
    if (!userEmail) return;

    const baseURL = process.env.REACT_APP_API_URL;
    try {
      const token = await fetchToken();
      const response = await fetch(
        `${baseURL}/api/nominations/users/${userEmail}/manager`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return;
      const data = await response.json();
      const all: any[] = data.responseData ?? [];

      const pending = (vote: string) => vote !== 'Approved' && vote !== 'Rejected';

      // Skip logic — same rules as MainContent.tsx and ReviewHubPage.tsx
      const managerSkipped  = (i: any) => i.nominatorEmail === i.managerEmail;
      const unitHeadSkipped = (i: any) => i.nominatorEmail === i.firstLevelApproverEmail;
      const eltNominated    = (i: any) => i.nominatorEmail === i.secondLevelApproverEmailId;
      const managerCleared  = (i: any) =>
        i.managerVote === 'Approved' || managerSkipped(i) || unitHeadSkipped(i) || eltNominated(i);
      const unitHeadCleared = (i: any) =>
        i.firstLevelApproverVote === 'Approved' || unitHeadSkipped(i) || eltNominated(i);

      let count = 0;

      // Stage 0/3 — Manager: not yet voted AND manager is not the nominator
      count += all.filter(
        item => item.managerEmail === userEmail &&
                !managerSkipped(item) &&
                pending(item.managerVote)
      ).length;

      // Stage 1/4 — Unit Head: prior stage cleared AND unit head is not the nominator
      count += all.filter(
        item => item.firstLevelApproverEmail === userEmail &&
                managerCleared(item) &&
                !unitHeadSkipped(item) &&
                pending(item.firstLevelApproverVote)
      ).length;

      // Stage 2/5 — ELT Review: both prior stages cleared
      count += all.filter(
        item => item.secondLevelApproverEmailId === userEmail &&
                managerCleared(item) &&
                unitHeadCleared(item) &&
                pending(item.secondLevelApproverVote)
      ).length;

      // Stage 6 — ELT Calibration: ELT approved AND final approver not yet voted
      count += all.filter(
        item => item.finalLevelApproverEmail === userEmail &&
                item.secondLevelApproverVote === 'Approved' &&
                pending(item.finalLevelApproverVote)
      ).length;

      // Stage 7 — Final Review: super admin only — fetch all nominations
      const adminRes = await fetch(`${baseURL}/api/nominations/users/${userEmail}/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (adminRes.ok) {
        const roleCode = await adminRes.json();
        if (roleCode === 5) {
          const allNomRes = await fetch(`${baseURL}/api/nominations`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (allNomRes.ok) {
            const allNomData = await allNomRes.json();
            const allNoms: any[] = allNomData.responseData ?? allNomData ?? [];
            count += allNoms.filter(
              item => item.rewardName === 'ACE Individual Award' &&
                      item.secondLevelApproverVote === 'Approved' &&
                      pending(item.finalLevelApproverVote)
            ).length;
          }
        }
      }

      setPendingCount(count);
    } catch {
      // silently ignore
    }
  }, [fetchToken]);

  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount]);

  if (!globalAwardsActive) return null;

  return (
    <Tooltip
      title="Pending Reviews"
      overlayInnerStyle={{ backgroundColor: '#8629A4', color: '#FFFFFF', fontSize: '12px', borderRadius: '4px' }}
    >
      <div
        onClick={() => navigate('/review-hub')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '30px',
          height: '30px',
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.25)',
          cursor: 'pointer',
        }}
      >
        <Badge count={pendingCount} size="small" offset={[4, -4]}>
          <FileDoneOutlined style={{ color: '#FFFFFF', fontSize: '16px' }} />
        </Badge>
      </div>
    </Tooltip>
  );
};

export default PendingReviewsBadge;
